import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { Router } from 'wouter';
import { Analytics } from '@vercel/analytics/react';
import ErrorBoundary from './components/common/ErrorBoundary';
import { supabase } from './config/supabase';

// 🩺 Error tracking (Sentry) — no-op silencieux tant que le DSN n'est pas fourni
// (voir docs/PRDs/PRD-observabilite-error-tracking.md)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });
}

// 🚀 PRODUCTION: Désactiver les logs verbeux sauf erreurs critiques
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  // ✅ Garder console.warn en prod pour diagnostiquer les problèmes
  // console.warn = () => {};
}

// 🔇 Capturer silencieusement les erreurs de Service Worker (ex: serveur non disponible)
// 🩺 Le reste des rejections non gérées part vers Sentry (échecs IndexedDB, réseau, etc.)
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || String(event.reason);
  if (reason.includes('ServiceWorker') || reason.includes('service-worker')) {
    event.preventDefault(); // Empêche l'affichage dans la console/overlay
    console.debug('📴 SW update silencieux:', reason);
    return;
  }

  if (event.reason instanceof Error) {
    Sentry.captureException(event.reason);
  } else {
    Sentry.captureMessage(`Unhandled promise rejection: ${reason}`, 'error');
  }
});

// 🩺 Tag Sentry avec l'utilisateur/organisateur connecté (pas d'email, pas de PII)
// Écouteur dédié et indépendant d'AuthContext pour ne pas toucher sa logique existante.
supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.user) {
    Sentry.setUser({ id: session.user.id });
  } else {
    Sentry.setUser(null);
  }
});

// 🐛 DEBUG: Why Did You Render pour tracer les re-renders
if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React, {
    trackAllPureComponents: false,
    trackHooks: true,
    logOnDifferentValues: true,
    include: [/useSmartGeoEvents/, /HomePage/, /GeolocationProvider/],
    exclude: [/^Route/, /^Router/, /^BrowserRouter/]
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Rendre l'app immédiatement (ne pas bloquer sur le Service Worker)
// ✅ ErrorBoundary global : attrape les erreurs React, retire le splash, affiche un écran de retry
root.render(
  <ErrorBoundary>
    <HelmetProvider>
      <Router>
        <App />
      </Router>
    </HelmetProvider>
    {/* Vercel Web Analytics (29/09) : sans cookie, ne compte que les navigateurs
        qui executent le JS, donc le vrai trafic humain face aux requetes CDN
        (quota Hobby a 75 %, 98,6 % venant de what2do, surtout des robots). */}
    <Analytics />
  </ErrorBoundary>
);

// Enregistrer le Service Worker en arrière-plan (non-bloquant)
const initializeServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return;

  // Vérifier si le serveur est accessible
  const isServerAvailable = async (): Promise<boolean> => {
    try {
      const response = await fetch('/service-worker.js', { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const serverAvailable = await isServerAvailable();

  if (!serverAvailable) {
    console.debug('📴 Mode offline - Service Worker existant utilisé');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('✅ Service Worker enregistré:', registration);

    // Vérifier les mises à jour immédiatement
    registration.update().catch(() => {});

    // Vérifier les mises à jour toutes les 5 minutes
    setInterval(() => {
      registration.update().catch(() => {});
    }, 5 * 60 * 1000);

    // Quand un nouveau SW est trouvé et installé
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        console.log('🔄 Nouveau Service Worker détecté, installation...');

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('🆕 Nouvelle version du SW installée, active au prochain chargement');
            // ✅ FIX: NE PLUS forcer skipWaiting + reload
            // Le SW a déjà skipWaiting() dans son install handler
            // Il prend le contrôle via clients.claim() sans reload
            // Les nouvelles ressources seront servies au prochain chargement naturel
          }
        });
      }
    });

    // ✅ FIX: NE PLUS recharger la page sur controllerchange
    // Le nouveau SW prend le contrôle silencieusement sans disruption.

    // 📦 Pre-cache les assets JS/CSS du build actuel pour le mode offline
    // Le SW ne connait pas les noms hashés des bundles, on les lui envoie
    if (navigator.serviceWorker.controller) {
      const scripts = document.querySelectorAll('script[src*="/static/js/"]');
      const styles = document.querySelectorAll('link[rel="stylesheet"][href*="/static/css/"]');
      scripts.forEach(s => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'CACHE_NEW_ROUTE',
          url: (s as HTMLScriptElement).src
        });
      });
      styles.forEach(l => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'CACHE_NEW_ROUTE',
          url: (l as HTMLLinkElement).href
        });
      });
    }

  } catch {
    console.debug('📴 Service Worker non disponible - mode dégradé');
  }
};

initializeServiceWorker();

// 📊 PRD-A (mesure d'affluence unifiée) : compteur server-side what2do.today.
// Fire-and-forget, 1x par session (sessionStorage, même pattern que
// 'w2d_org_login_tracked' dans OrganizerContext.tsx) — ne doit jamais bloquer
// ni faire échouer le chargement de l'app. Tant que les variables d'env Vercel
// (SUPABASE_URL/SUPABASE_ANON_KEY/REDIRECT_LOG_INTERNAL_KEY/IP_HASH_SALT) ne
// sont pas créées côté api/track-visit.js, ce call est un no-op silencieux
// (l'endpoint répond 202 sans écrire en base) — voir api/track-visit.js.
const trackMainSiteVisit = () => {
  // Bots/headless qui exécutent le JS (crawlers IA type meta-externalagent,
  // Puppeteer, Lighthouse…) : ne pas compter — même garde que l'analytics
  // touriste (AnalyticsBatchService.isBotSession). Le serveur classe aussi par
  // UA (log_redirect, migration 086), ceci évite d'écrire la ligne du tout.
  try {
    if ((navigator as any).webdriver === true) return;
    if (/bot|crawl|spider|slurp|headless|lighthouse|pagespeed|pingdom|prerender|phantomjs|puppeteer|playwright|selenium|screenshot|gtmetrix|uptime|monitor|dataprovider|facebookexternalhit|meta-external|whatsapp|telegrambot/i.test(navigator.userAgent || '')) return;
  } catch {
    // Détection impossible : on continue (le serveur classifiera par UA).
  }

  try {
    if (sessionStorage.getItem('w2d_visit_tracked')) return;
    sessionStorage.setItem('w2d_visit_tracked', '1');
  } catch {
    // sessionStorage indisponible (navigation privée stricte) : on ne track pas
    // plutôt que de risquer un appel par navigation.
    return;
  }

  const path = encodeURIComponent(window.location.pathname || '/');
  fetch(`/api/track-visit?path=${path}`, { method: 'GET', keepalive: true }).catch(() => {
    // Silencieux : un échec réseau ne doit jamais impacter l'UX.
  });
};

trackMainSiteVisit();